import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {
  Button,
  InputTextFormik,
  SelectFormik,
  TransparentButton,
  ValidationErrorModal,
} from '../../../../../_gori/partials/widgets'
import * as Yup from 'yup'
import UseYupValidate from '../../../../../_gori/hooks/UseYupValidate'
import {find, forEach, includes, isEmpty} from 'lodash'
import {CSSTransition} from 'react-transition-group'
import {PROFILE_FIELD} from '../../core/_const'
import {TIER_CODE_ENTERPRISE} from '../../../common/core/_const'
import {AutocompleteAddress} from '../../../common'
import {STEPS} from '../../core/_const'
import {KTSVG} from '../../../../../_gori/helpers'
import {Industry, MonthlyShipmentVolume} from '../../../settings/core/_const'

type Props = {
  setActiveStep: any
  onboard?: any
  prepareData?: any
  setPrepareData?: any
  currentTier: any
  originalFormik: any
  disable: any
  saveAndExit: any
  setSaveAndExit: any
}

const Onboarding: React.FC<Props> = ({
  setActiveStep,
  onboard,
  setPrepareData,
  currentTier,
  originalFormik,
  disable,
  saveAndExit,
  setSaveAndExit,
}) => {
  const intl = useIntl()
  const {stringYup, regexYup} = UseYupValidate()
  const [validationErrors, setValidationErrors] = useState<any>()

  const validationSchema = useMemo(() => {
    let required = true

    return Yup.object().shape({
      country: stringYup(255, 'COUNTRY', required),
      street1: stringYup(255, 'ADDRESS', required),
      street2: stringYup(255, 'ADDRESS_2', false),
      city: stringYup(255, 'CITY', required),
      zip: Yup.string().when(`country`, {
        is: (val) => val === 'US',
        then: regexYup.zipCode('POSTAL_CODE', required),
        otherwise: stringYup(255, 'POSTAL_CODE', required),
      }),
      state: Yup.string().when(`country`, {
        is: (val) => val === 'US',
        then: regexYup.state('STATE', required),
        otherwise: stringYup(255, 'STATE', required),
      }),
      monthly_shipment_volume: stringYup(255, 'SHIPMENT_VOLUME', required),
      industry: stringYup(255, 'INDUSTRY', required),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveAndExit])

  const initialValues = useMemo(() => {
    let init: any = {}
    forEach([...PROFILE_FIELD.SALES, ...PROFILE_FIELD.BUSINESS_ADDRESS], (field: any) => {
      if (!isEmpty(onboard)) {
        init[field.name] = onboard?.profile?.[`${field.name}`]
      }
    })

    init['monthly_shipment_volume'] = onboard?.profile?.monthly_shipment_volume
    init['industry'] = onboard?.profile?.industry

    return {
      ...init,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboard])

  const temporarilySaveOnboardData = (data: any) => {
    forEach([...PROFILE_FIELD.SALES, ...PROFILE_FIELD.BUSINESS_ADDRESS], (field: any) => {
      if (!isEmpty(onboard)) {
        onboard.profile[`${field.name}`] = data?.[`${field.name}`]
      }
    })

    onboard.profile['monthly_shipment_volume'] = data?.monthly_shipment_volume
    onboard.profile['industry'] = data?.industry
  }

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)

      let payload = {
        step: 'single_step',
        data: {
          ...values,
          check_hazardous: true,
        },
        save_and_exit: saveAndExit,
      }

      temporarilySaveOnboardData(payload.data)
      setPrepareData(payload.data)
      setSubmitting(false)

      if (currentTier === TIER_CODE_ENTERPRISE) {
        setActiveStep(STEPS.selected_plan)
      } else {
        originalFormik.submitForm()
      }
    },
  })

  const selectedPlace = (selected) => {
    let city = find(selected.address_components, (item) => {
      return includes(item?.types, 'locality')
    })
    formik.setFieldValue(formik.getFieldProps(`city`).name, city?.long_name ?? '')
    let state = find(selected.address_components, (item) => {
      return includes(item?.types, 'administrative_area_level_1')
    })
    formik.setFieldValue(formik.getFieldProps(`state`).name, state?.short_name ?? '')
    let country = find(selected.address_components, (item) => {
      return includes(item?.types, 'country')
    })
    formik.setFieldValue(formik.getFieldProps(`country`).name, country?.short_name ?? '')
    let zipCode = find(selected.address_components, (item) => {
      return includes(item?.types, 'postal_code')
    })
    let suffix = find(selected.address_components, (item) => {
      return includes(item?.types, 'postal_code_suffix')
    })
    if (zipCode?.long_name) {
      zipCode = zipCode?.long_name + (suffix?.long_name ? `-${suffix?.long_name}` : '')
    } else {
      zipCode = ''
    }
    formik.setFieldValue('zip', zipCode)
  }

  const handleContinue = () => {
    setSaveAndExit(false)
    formik.handleSubmit()
  }

  const handleSaveAndEdit = async () => {
    setSaveAndExit(true)
    formik.handleSubmit()
  }

  const backStep = () => {
    setActiveStep(STEPS.pricing)
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <>
          <div className='pricing-step-wrapper d-flex justify-content-center align-items-center my-9'>
            <div className='w-100 pricing-step-card'>
              <div className='pricing-step-card__body'>
                <div className='col-md-12 bg-white rounded-3 rounded-bottom-0'>
                  <div className='text-start fs-1 fw-bolder text-black px-10 py-5 border-bottom'>
                    {intl.formatMessage({id: 'SINGLE_STEP_TITLE'})}
                  </div>
                </div>

                <div className='col-md-12 px-18 py-12'>
                  <div className='text-start'>
                    <p className='fs-3 fw-bolder'>{intl.formatMessage({id: 'BUSINESS_ADDRESS'})}</p>
                    <div className='row pt-5 pb-10'>
                      {PROFILE_FIELD.BUSINESS_ADDRESS.map((field, index) => {
                        if (field.type === 'text') {
                          return (
                            <div className={`my-2 ${field?.col}`} key={index}>
                              <InputTextFormik
                                className='col-12'
                                formik={formik}
                                name={field.name}
                                disabled={formik.isSubmitting}
                                label={intl.formatMessage({id: field.label})}
                                labelClassName={clsx('fw-bolder', {required: field.required})}
                              />
                            </div>
                          )
                        }
                        if (field.type === 'select') {
                          return (
                            <div className={`my-2 ${field?.col}`} key={index}>
                              <SelectFormik
                                checkFormik
                                placeholder={''}
                                options={field?.options || []}
                                formik={formik}
                                name={field.name}
                                required={field.required}
                                label={intl.formatMessage({id: field.label})}
                                labelClassName={clsx('fw-bolder', {required: field.required})}
                              />
                            </div>
                          )
                        }
                        if (field.type === 'address') {
                          return (
                            <div className={`my-2 ${field?.col} address-complete`} key={index}>
                              <label
                                className={`form-label fw-bolder ${
                                  field.required ? 'required' : ''
                                }`}
                              >
                                {intl.formatMessage({id: field.label})}
                                {!field.required && (
                                  <i className='ms-2 text-muted fs-7'>
                                    {intl.formatMessage({id: 'OPTIONAL_LABEL'})}
                                  </i>
                                )}
                              </label>
                              <AutocompleteAddress
                                formik={formik}
                                name={field.name}
                                label=''
                                onSelected={(selected) => {
                                  selectedPlace(selected)
                                }}
                              />
                            </div>
                          )
                        }
                        return null
                      })}
                    </div>
                  </div>
                  <div>
                    <p className='fs-3 fw-bolder'>
                      {intl.formatMessage({id: 'BUSINESS_BACKGROUND'})}
                    </p>
                    <div className='row pt-8 pb-10'>
                      <div className='pb-5'>
                        <SelectFormik
                          className={'mb-5'}
                          checkFormik
                          emptyDefault={false}
                          options={MonthlyShipmentVolume}
                          formik={formik}
                          name='monthly_shipment_volume'
                          required={true}
                          isSearchable={false}
                          hasUseIntl={true}
                          label={intl.formatMessage({id: 'SHIPMENT_VOLUME'})}
                          placeholder={intl.formatMessage({id: 'SELECT_VOLUME'})}
                          labelClassName='fw-bolder required text-dark'
                        />
                      </div>
                      <div className='pb-5'>
                        <SelectFormik
                          className={'mb-5'}
                          checkFormik
                          emptyDefault={false}
                          options={Industry}
                          formik={formik}
                          name='industry'
                          required={true}
                          isSearchable={false}
                          hasUseIntl={true}
                          label={intl.formatMessage({id: 'INDUSTRY'})}
                          placeholder={intl.formatMessage({id: 'SELECT_AN_INDUSTRY'})}
                          labelClassName='fw-bolder required text-dark'
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='px-5 py-5 bg-white rounded-3 rounded-top-0 border-top'>
                  <div className='d-flex justify-content-end container'>
                    <Button
                      className='btn btn-advanced-primary'
                      label={intl.formatMessage({id: 'LET_GET_SHIPPING'})}
                      loadingText={intl.formatMessage({id: 'LET_GET_SHIPPING'})}
                      loading={formik.isSubmitting}
                      event={handleContinue}
                      disabled={disable}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='d-block'>
            <div className='save-and-exit d-flex justify-content-end'>
              <TransparentButton
                className='btn onboard-form'
                label={intl.formatMessage({id: 'BACK'})}
                loadingText={intl.formatMessage({id: 'BACK'})}
                loading={disable}
                event={() => backStep()}
                disabled={disable}
                icon={<KTSVG path='/media/gori/arrows/arrow_left.svg' />}
              ></TransparentButton>
              <TransparentButton
                className='btn me-20'
                label={intl.formatMessage({id: 'SAVE_AND_EXIT'})}
                loadingText={intl.formatMessage({id: 'SAVE_AND_EXIT'})}
                loading={disable}
                event={handleSaveAndEdit}
                disabled={disable}
              >
                <KTSVG
                  path='/media/gori/orders/delete.svg'
                  className='m-0'
                  svgClassName='mh-10px'
                />
              </TransparentButton>
            </div>
          </div>
        </>
      </CSSTransition>
    </>
  )
}

export {Onboarding}
